import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useBot } from "@/context/BotContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { playWinSound, playLossSound } from "@/utils/sounds";
import {
  Play, Square, TrendingUp, TrendingDown,
  Clock, Target, Activity, ChevronRight,
  Volume2, VolumeX, RefreshCw, AlertTriangle, Trash2,
} from "lucide-react";

const API = "/api";

const timeAgo = (ts) => {
  if (!ts) return "";
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
};

const fmtTime = (secs) => {
  if (secs == null || secs <= 0) return "0:00";
  return `${Math.floor(secs / 60)}:${String(Math.floor(secs % 60)).padStart(2, "0")}`;
};

const Dashboard = () => {
  const { token } = useAuth();
  const { selectedBotId, mode, selectedBot, fetchBots } = useBot();
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [preview, setPreview] = useState(null);
  const [market, setMarket] = useState(null);
  const [trades, setTrades] = useState([]);
  const [tradeData, setTradeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const prevTradeCount = useRef(0);
  const soundOnRef = useRef(soundOn);
  soundOnRef.current = soundOn;

  const headersRef = useRef({});
  headersRef.current = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchCore = useCallback(async () => {
    if (!selectedBotId) { setLoading(false); return; }
    const h = headersRef.current;
    const bp = { bot_id: selectedBotId };
    const timeout = (promise) => Promise.race([promise, new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), 8000))]);
    try {
      const [s, st, tr] = await Promise.all([
        timeout(axios.get(`${API}/bot/status`, { params: bp, headers: h })),
        timeout(axios.get(`${API}/settings`, { params: bp, headers: h })),
        timeout(axios.get(`${API}/trades`, { params: { ...bp, limit: 15 }, headers: h })),
      ]);
      setStatus(s.data);
      setSettings(st.data);
      const newTrades = tr.data.trades || [];
      if (prevTradeCount.current > 0 && newTrades.length > 0 && tr.data.total > prevTradeCount.current) {
        const latest = newTrades[0];
        if (soundOnRef.current) { latest.outcome === "win" ? playWinSound() : playLossSound(); }
      }
      prevTradeCount.current = tr.data.total;
      setTrades(newTrades);
      setTradeData(tr.data);
    } catch (e) { console.error("Dashboard fetch:", e.message); }
    finally { setLoading(false); }
  }, [selectedBotId]);

  const fetchPreview = useCallback(async () => {
    if (!selectedBotId) return;
    try {
      const h = headersRef.current;
      const [p, m] = await Promise.all([
        axios.get(`${API}/market/preview-trade?bot_id=${selectedBotId}`, { headers: h }),
        axios.get(`${API}/bot/active-market?bot_id=${selectedBotId}`, { headers: h }),
      ]);
      setPreview(p.data);
      setMarket(m.data);
    } catch {}
  }, [selectedBotId]);

  useEffect(() => {
    fetchCore(); fetchPreview();
    const i1 = setInterval(fetchCore, 5000);
    const i2 = setInterval(fetchPreview, 4000);
    return () => { clearInterval(i1); clearInterval(i2); };
  }, [fetchCore, fetchPreview]);

  const startBot = async () => {
    if (mode === "live" && !window.confirm("Start LIVE trading? This places REAL orders on Polymarket.")) return;
    setActionLoading(true);
    try {
      const params = selectedBotId ? `bot_id=${selectedBotId}` : `mode=${mode}`;
      await axios.post(`${API}/bot/start?${params}`, {}, { headers: headersRef.current });
      toast.success(`Bot started — ${mode.toUpperCase()}`); fetchCore(); fetchBots();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to start"); }
    finally { setActionLoading(false); }
  };

  const stopBot = async () => {
    setActionLoading(true);
    try {
      const params = selectedBotId ? `bot_id=${selectedBotId}` : `mode=${mode}`;
      await axios.post(`${API}/bot/stop?${params}`, {}, { headers: headersRef.current });
      toast.success("Bot stopped"); fetchCore(); fetchBots();
    } catch { toast.error("Failed to stop"); } finally { setActionLoading(false); }
  };

  const resetAll = async () => {
    if (!window.confirm("Reset ladder to Step 1? This stops the bot. Trade history is preserved.")) return;
    try {
      const params = selectedBotId ? `bot_id=${selectedBotId}` : `mode=${mode}`;
      await axios.post(`${API}/bot/reset-all?${params}`, {}, { headers: headersRef.current });
      toast.success("Reset complete"); fetchCore(); fetchBots();
    } catch { toast.error("Failed to reset"); }
  };

  const clearAllData = async () => {
    if (!window.confirm(`DELETE all trade history for this bot and reset all stats? This cannot be undone.`)) return;
    try {
      const params = selectedBotId ? `bot_id=${selectedBotId}` : `mode=${mode}`;
      await axios.post(`${API}/bot/clear-all-data?${params}`, {}, { headers: headersRef.current });
      toast.success("All data cleared — fresh start"); fetchCore(); fetchBots();
    } catch { toast.error("Failed to clear data"); }
  };

  const botStatus = status?.status;
  const isRunning = ["running", "cooling_down", "waiting_for_market", "in_trade"].includes(botStatus);
  const botMode = mode;
  const botActivity = status?.bot_activity;
  const hasCreds = selectedBot?.has_private_key || settings?.has_polymarket_private_key;
  const showBotUI = isRunning;

  const resolvedTrades = trades;
  // Total P/L and total stats computed from ALL trade records server-side (not stale counters)
  const computedStats = tradeData?.computed_stats || {};
  const modePnl = computedStats.total_pnl ?? resolvedTrades.reduce((s, t) => s + (t.profit_loss || t.profit || 0), 0);
  const modeTotal = computedStats.total_trades ?? 0;
  const modeWins = computedStats.wins ?? resolvedTrades.filter(t => t.outcome === "win").length;
  const modeLosses = computedStats.losses ?? resolvedTrades.filter(t => t.outcome === "loss").length;
  // Daily P/L computed from fetched trades filtered to today
  const modeDailyPnl = (() => {
    const today = new Date().toISOString().slice(0, 10);
    return trades.filter(t => t.timestamp?.startsWith(today)).reduce((s, t) => s + (t.profit_loss || t.profit || 0), 0);
  })();
  const modeDailyTrades = (() => {
    const today = new Date().toISOString().slice(0, 10);
    return trades.filter(t => t.timestamp?.startsWith(today) && (t.outcome === "win" || t.outcome === "loss")).length;
  })();
  const winRate = modeTotal > 0 ? ((modeWins / modeTotal) * 100).toFixed(1) : "0.0";
  const modeStreak = (() => {
    const resolved = trades.filter(t => t.outcome === "win" || t.outcome === "loss");
    if (resolved.length === 0) return 0;
    let streak = 0;
    const first = resolved[0]?.outcome;
    for (const t of resolved) {
      if (t.outcome === first) streak += first === "win" ? 1 : -1;
      else break;
    }
    return streak;
  })();

  const currentLivePct = preview?.trade_analysis?.entry_percent;

  const getLadder = () => {
    const fb = settings?.fade_bets;
    return Array.isArray(fb) && fb.length === 2 ? fb.map(Number) : [10, 20];
  };
  const ladder = getLadder();
  const step = status?.tf_phase === "bet2" ? 2 : 1;

  const cooldownLeft = () => {
    if (!status?.cooldown_end_time) return null;
    const d = new Date(status.cooldown_end_time) - Date.now();
    return d > 0 ? fmtTime(d / 1000) : null;
  };
  const cd = cooldownLeft();

  const btcPrice = preview?.market?.btc_price;
  const btcPct = preview?.market?.btc_change_pct;
  const btcDir = preview?.market?.btc_direction;

  const [timeLeft, setTimeLeft] = useState(null);
  const endTimeRef = useRef(null);
  const lastRefreshRef = useRef(0);

  useEffect(() => {
    const candidates = [market?.end_time, preview?.market?.end_time].filter(Boolean);
    if (candidates.length > 0) {
      const now = Date.now();
      const parsed = candidates.map(t => new Date(t).getTime()).sort((a, b) => b - a);
      const future = parsed.find(t => t > now);
      if (future) endTimeRef.current = future;
      else if (!endTimeRef.current || parsed[0] > endTimeRef.current) endTimeRef.current = parsed[0];
    }
  }, [market?.end_time, preview?.market?.end_time]);

  useEffect(() => {
    const tick = () => {
      if (!endTimeRef.current) return;
      const remaining = (endTimeRef.current - Date.now()) / 1000;
      setTimeLeft(remaining > 0 ? remaining : 0);
      if (remaining <= 0) {
        const now = Date.now();
        if (now - lastRefreshRef.current > 5000) { lastRefreshRef.current = now; fetchPreview(); }
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [fetchPreview]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div style={{ color: 'var(--accent-teal)' }} className="text-sm animate-pulse">Connecting to market...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-5">
        {/* ═══ TOP BAR ═══ */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div data-testid="bot-status-badge" className="inline-flex items-center gap-2 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-lg"
              style={{
                background: showBotUI ? 'rgba(0, 229, 168, 0.06)' : 'rgba(30, 42, 58, 0.5)',
                border: `1px solid ${showBotUI ? 'rgba(0, 229, 168, 0.2)' : 'var(--border)'}`,
                color: showBotUI && botStatus === "in_trade" ? 'var(--accent-cyan)'
                  : showBotUI && botStatus === "waiting_for_market" ? 'var(--accent-yellow)'
                  : showBotUI ? 'var(--accent-teal)' : 'var(--text-secondary)',
              }}>
              <span className="w-2 h-2 rounded-full" style={{
                background: showBotUI ? 'var(--accent-teal)' : '#3a4555',
                boxShadow: showBotUI ? '0 0 8px rgba(0, 229, 168, 0.4)' : 'none',
              }} />
              {showBotUI && botStatus === "cooling_down" ? "COOLDOWN"
                : showBotUI && botStatus === "in_trade" ? "IN TRADE"
                : showBotUI && botStatus === "waiting_for_market" ? "WAITING"
                : showBotUI ? "RUNNING" : "STOPPED"}
            </div>
            <span className="text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-md"
              style={{
                border: `1px solid ${mode === "live" ? 'rgba(0, 229, 168, 0.25)' : 'rgba(0, 194, 255, 0.25)'}`,
                color: mode === "live" ? 'var(--accent-teal)' : 'var(--accent-cyan)',
              }}>{selectedBot?.name || mode}</span>
            <span data-testid="mode-badge" className="text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-md"
              style={{
                background: mode === "live" ? 'rgba(0, 229, 168, 0.15)' : 'rgba(0, 194, 255, 0.15)',
                border: `1px solid ${mode === "live" ? 'rgba(0, 229, 168, 0.45)' : 'rgba(0, 194, 255, 0.45)'}`,
                color: mode === "live" ? '#00E5A8' : '#00C2FF',
              }}
              title={mode === "live" ? "LIVE — places real orders on Polymarket" : "PAPER — simulated trading, NO real money"}>
              {mode === "live" ? "● LIVE" : "○ PAPER"}
            </span>
          </div>

          <div className="flex items-center gap-2">
            {isRunning ? (
              <Button data-testid="stop-bot-btn" onClick={stopBot} disabled={actionLoading}
                className="h-9 px-4 text-xs font-semibold uppercase rounded-lg"
                style={{ background: 'rgba(239, 68, 68, 0.85)', color: '#ffffff', border: '1px solid rgba(239, 68, 68, 0.9)', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                <Square className="w-3.5 h-3.5 mr-1.5" />{actionLoading ? "..." : "STOP"}
              </Button>
            ) : (
              <Button data-testid="start-bot-btn" onClick={startBot}
                disabled={actionLoading || (mode === "live" && !hasCreds)}
                className="btn-primary h-9 px-5 text-xs" style={{ color: '#0B0F1A' }}>
                <Play className="w-3.5 h-3.5 mr-1.5" />
                {mode === "live" ? "Start Live" : "Start Paper"}
              </Button>
            )}
            <button data-testid="toggle-sound-btn" onClick={() => setSoundOn(!soundOn)}
              className="p-1.5 transition-colors rounded-md" style={{ color: 'var(--text-secondary)' }}>
              {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            <button data-testid="reset-btn" onClick={resetAll}
              className="p-1.5 transition-colors rounded-md" style={{ color: 'var(--text-secondary)' }} title="Reset ladder">
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
            <button data-testid="clear-all-btn" onClick={clearAllData}
              className="p-1.5 transition-colors rounded-md" style={{ color: 'var(--accent-red, #ef4444)' }} title="Clear all data">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Notices */}
        {mode === "paper" && (
          <div data-testid="paper-mode-banner" className="rounded-lg px-4 py-2 text-xs font-semibold flex items-center gap-2"
            style={{ background: 'rgba(0, 194, 255, 0.08)', border: '1px solid rgba(0, 194, 255, 0.25)', color: '#00C2FF' }}>
            <span className="text-base">○</span>
            <span>This bot is in <strong className="uppercase tracking-wider">paper mode</strong> — trades are simulated. No real money. No orders placed on Polymarket.</span>
          </div>
        )}
        {mode === "live" && !hasCreds && (
          <div className="rounded-lg px-4 py-3 text-xs" style={{ background: 'rgba(30, 42, 58, 0.5)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
            Add Polymarket API credentials in <a href="/settings" style={{ color: 'var(--accent-teal)' }} className="hover:underline">Settings</a> to enable live trading.
          </div>
        )}
        {showBotUI && cd && (
          <div className="rounded-lg px-4 py-3 flex items-center gap-2 text-xs"
            style={{ background: 'rgba(245, 158, 11, 0.06)', border: '1px solid rgba(245, 158, 11, 0.2)', color: 'var(--accent-yellow)' }}>
            <Clock className="w-4 h-4" /> Ladder failed — cooldown: <span className="font-bold">{cd}</span>
          </div>
        )}
        {/* Filter skip banner removed per user request */}
        {showBotUI && (status?.pause_reason === "insufficient_balance" || preview?.balance?.pause_reason === "insufficient_balance") && (
          <div data-testid="balance-warning-banner" className="rounded-lg px-4 py-3 flex items-center gap-2 text-xs"
            style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', color: 'var(--accent-red)' }}>
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>
              <strong>INSUFFICIENT USDC BALANCE</strong> — Your Polymarket balance
              {preview?.balance?.usdc_balance != null && <> (${Number(preview.balance.usdc_balance).toFixed(2)})</>} is
              too low for the current bet size. Deposit more USDC on Polymarket to resume trading.
            </span>
          </div>
        )}
        {showBotUI && botActivity && (
          <div data-testid="bot-activity-bar" className="rounded-lg px-4 py-3 flex items-center gap-2 text-xs"
            style={{
              background: botActivity?.includes("live_order_failed") || botActivity?.includes("order_failed_retrying")
                ? 'rgba(239, 68, 68, 0.06)' : botActivity?.includes("filter_skip")
                ? 'rgba(245, 158, 11, 0.06)' : botStatus === "in_trade" ? 'rgba(0, 194, 255, 0.06)'
                : botStatus === "waiting_for_market" ? 'rgba(245, 158, 11, 0.06)' : 'rgba(30, 42, 58, 0.4)',
              border: `1px solid ${botActivity?.includes("live_order_failed") || botActivity?.includes("order_failed_retrying")
                ? 'rgba(239, 68, 68, 0.2)' : botActivity?.includes("filter_skip")
                ? 'rgba(245, 158, 11, 0.3)' : botStatus === "in_trade" ? 'rgba(0, 194, 255, 0.2)'
                : botStatus === "waiting_for_market" ? 'rgba(245, 158, 11, 0.2)' : 'var(--border)'}`,
              color: botActivity?.includes("live_order_failed") || botActivity?.includes("order_failed_retrying")
                ? 'var(--accent-red)' : botActivity?.includes("filter_skip")
                ? 'var(--accent-yellow)' : botStatus === "in_trade" ? 'var(--accent-cyan)'
                : botStatus === "waiting_for_market" ? 'var(--accent-yellow)' : 'var(--text-secondary)',
            }}>
            <Activity className="w-4 h-4 flex-shrink-0" />
            <span className="uppercase tracking-wider truncate">
              {botActivity?.includes("live_order_failed") || botActivity?.includes("order_failed_retrying")
                ? <>ORDER FAILED — <span style={{ color: 'var(--accent-yellow)' }} className="normal-case">{botActivity.split(": ").slice(1).join(": ").substring(0, 150) || "Retrying on next market"}</span></>
                : botActivity?.includes("filter_skip")
                ? <>FILTER SKIP — <span style={{ color: 'var(--text-secondary)' }} className="normal-case">{botActivity.replace("filter_skip ", "")}</span></>
                : botActivity}
            </span>
            {botStatus === "in_trade" && status?.trade_entry_btc && (
              <div className="ml-auto flex items-center gap-3 text-xs">
                <span style={{ color: 'var(--text-secondary)' }}>Entry: ${status.trade_entry_btc.toLocaleString()}</span>
                {currentLivePct && status?.current_contract_percent && (() => {
                  const entryPct = status.current_contract_percent / 100;
                  const currPct = currentLivePct / 100;
                  const betAmt = status.current_bet_amount || 0;
                  const pnlAmt = betAmt * (currPct / entryPct - 1);
                  const pnlPct = ((currPct / entryPct) - 1) * 100;
                  return (
                    <span className="font-bold" style={{ color: pnlAmt >= 0 ? 'var(--accent-teal)' : 'var(--accent-red)' }}>
                      {pnlAmt >= 0 ? "+" : ""}${pnlAmt.toFixed(2)} ({pnlPct >= 0 ? "+" : ""}{pnlPct.toFixed(1)}%)
                    </span>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* ═══ BENTO GRID ═══ */}
        <div className="grid grid-cols-12 gap-4">
          {/* ──── MARKET PANEL ──── */}
          <div className="col-span-12 lg:col-span-8 space-y-4">
            {/* BTC price */}
            <div data-testid="market-panel" className="card-base">
              <div className="card-content">
                <div className="flex flex-wrap items-end justify-between gap-4">
                  <div>
                    <div className="label-style">BTC/USD</div>
                    <div className="stat-number" style={{ color: 'var(--text-primary)' }}>
                      {btcPrice && btcPrice > 0 ? `$${btcPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "Loading..."}
                    </div>
                    <div className="flex items-center gap-1 text-sm mt-1" style={{
                      color: btcDir === "UP" ? 'var(--accent-teal)' : btcDir === "DOWN" ? 'var(--accent-red)' : 'var(--text-secondary)',
                    }}>
                      {btcDir === "UP" ? <TrendingUp className="w-4 h-4" /> : btcDir === "DOWN" ? <TrendingDown className="w-4 h-4" /> : null}
                      {btcPct != null ? `${btcPct > 0 ? "+" : ""}${btcPct.toFixed(3)}%` : "--"} <span className="ml-1" style={{ color: 'var(--text-secondary)' }}>{preview?.market?.window_minutes ? `${preview.market.window_minutes}m` : `${selectedBot?.window_minutes || 15}m`}</span>
                    </div>
                  </div>
                  <div className="flex gap-6 text-right">
                    <div>
                      <div className="label-style">Up / Down</div>
                      <div className="text-sm">
                        <span style={{ color: 'var(--accent-teal)' }}>{preview?.market?.up_price != null ? `${preview.market.up_price}%` : "--"}</span>
                        <span className="mx-1" style={{ color: 'var(--text-secondary)' }}>/</span>
                        <span style={{ color: 'var(--accent-red)' }}>{preview?.market?.down_price != null ? `${preview.market.down_price}%` : "--"}</span>
                      </div>
                    </div>
                    <div>
                      <div className="label-style">Window</div>
                      <div className="text-sm" style={{ color: 'var(--text-primary)' }}>{
                        (() => {
                          const st = market?.start_time || preview?.market?.start_time;
                          const et = market?.end_time || preview?.market?.end_time;
                          if (st && et) { const s = new Date(st); const e = new Date(et); const fmt = (d) => d.toISOString().slice(11, 16); return `${fmt(s)}-${fmt(e)} UTC`; }
                          return "--";
                        })()
                      }</div>
                    </div>
                    <div>
                      <div className="label-style">Time Left</div>
                      <div className="text-sm font-bold" style={{ color: timeLeft != null && timeLeft > 0 && timeLeft < 30 ? 'var(--accent-red)' : 'var(--text-primary)' }}>
                        {timeLeft != null && timeLeft <= 0
                          ? <span className="animate-pulse text-xs" style={{ color: 'var(--accent-yellow)' }}>
                              {showBotUI && botStatus === "in_trade" ? "Resolving..." : "Next window..."}
                            </span>
                          : timeLeft != null ? fmtTime(timeLeft) : "--"}
                      </div>
                    </div>
                    <div>
                      <div className="label-style">Source</div>
                      <div className="text-xs" style={{ color: 'var(--accent-teal)' }}>
                        {market?.source === "polymarket" || preview?.market?.source === "polymarket" ? "Polymarket" : "Synthetic"}
                        {(market?.price_source || preview?.market?.price_source) === "clob_midpoint" && (
                          <span className="ml-1" style={{ color: 'var(--text-secondary)' }}>CLOB</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Trade entry info */}
            {preview && (
              <div data-testid="trade-preview" className="card-base">
                <div className="card-content">
                  <div className="grid grid-cols-2 sm:grid-cols-6 gap-4">
                    <div>
                      <div className="label-style">Direction</div>
                      <div className="text-lg font-bold" style={{ color: preview.trade_analysis?.your_direction === "UP" ? 'var(--accent-teal)' : 'var(--accent-red)' }}>
                        {preview.trade_analysis?.your_direction || "--"}</div>
                    </div>
                    <div>
                      <div className="label-style">Live %</div>
                      <div className="text-lg font-bold" style={{ color: preview.trade_analysis?.can_enter ? 'var(--accent-teal)' : 'var(--text-secondary)' }}>
                        {preview.trade_analysis?.entry_percent != null ? `${preview.trade_analysis.entry_percent}%` : "--"}</div>
                    </div>
                    <div>
                      <div className="label-style">Entry</div>
                      {showBotUI && botStatus === "in_trade" && preview.trade_analysis?.bot_entry_pct != null ? (
                        <div className="text-sm mt-0.5" style={{ color: 'var(--accent-cyan)' }}>Entered @ {preview.trade_analysis.bot_entry_pct.toFixed(1)}%</div>
                      ) : (
                        <>
                          <div className="text-sm mt-0.5" style={{ color: 'var(--text-primary)' }}>
                            {preview.trade_analysis?.entry_min != null ? `${preview.trade_analysis.entry_min}% - ${preview.trade_analysis.entry_max}%` : "--"}
                          </div>
                          <div className="text-[10px] font-semibold mt-0.5" style={{ color: preview.trade_analysis?.can_enter ? 'var(--accent-teal)' : 'var(--accent-red)' }}>
                            {preview.trade_analysis?.can_enter ? "IN RANGE" : "OUT OF RANGE"}</div>
                        </>
                      )}
                    </div>
                    <div>
                      <div className="label-style">Bet</div>
                      <div className="text-lg font-bold" style={{ color: 'var(--accent-cyan)' }}>${(ladder[step - 1] || ladder[0]).toFixed(2)}</div>
                    </div>
                    <div>
                      <div className="label-style">Potential Win</div>
                      <div className="text-lg font-bold" style={{ color: 'var(--accent-teal)' }}>+${preview.position?.potential_win?.toFixed(2) || "0"}</div>
                    </div>
                    <div>
                      <div className="label-style">Alignment</div>
                      <div className="text-xs font-semibold mt-1 inline-flex items-center px-2 py-0.5 rounded-md"
                        style={{
                          background: preview.prediction?.alignment === "ALIGNED" ? 'rgba(0, 229, 168, 0.06)' : 'rgba(245, 158, 11, 0.06)',
                          border: `1px solid ${preview.prediction?.alignment === "ALIGNED" ? 'rgba(0, 229, 168, 0.2)' : 'rgba(245, 158, 11, 0.2)'}`,
                          color: preview.prediction?.alignment === "ALIGNED" ? 'var(--accent-teal)' : 'var(--accent-yellow)',
                        }}>{preview.prediction?.alignment || "--"}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ──── TRADE FEED ──── */}
            <div data-testid="trade-feed" className="card-base">
              <div className="card-header flex items-center justify-between">
                <span className="label-style mb-0">Trade Feed</span>
                <div className="flex items-center gap-2">
                  {isRunning && <span className="flex items-center gap-1 text-[10px] font-semibold" style={{ color: 'var(--accent-teal)' }}>
                    <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: 'var(--accent-teal)' }} />{mode.toUpperCase()}</span>}
                  <span className="text-[10px]" style={{ color: 'var(--text-secondary)' }}>{trades.length} trades</span>
                </div>
              </div>
              <div className="max-h-[340px] overflow-y-auto">
                {trades.length === 0 ? (
                  <div className="p-8 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>No trades yet — start the bot</div>
                ) : (<>
                  <div className="grid grid-cols-[26px_58px_46px_minmax(0,1fr)_44px_auto] sm:grid-cols-[32px_80px_60px_minmax(0,1fr)_80px_auto] items-center gap-2 px-4 py-1.5 text-[9px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)' }}>
                    <span></span><span>P/L</span><span>Bet</span><span>Details</span><span>Result</span><span>Time</span>
                  </div>
                  {trades.map((t, i) => (
                  <div key={t.id || i} data-testid={`trade-row-${i}`}
                    className={`grid grid-cols-[26px_58px_46px_minmax(0,1fr)_44px_auto] sm:grid-cols-[32px_80px_60px_minmax(0,1fr)_80px_auto] items-center gap-2 px-4 py-2.5 table-row ${i === 0 ? "animate-fade-in" : ""}`}>
                    <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 rounded-md"
                      style={{
                        background: t.outcome === "win" ? 'rgba(0, 229, 168, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                        color: t.outcome === "win" ? 'var(--accent-teal)' : 'var(--accent-red)',
                      }}>
                      {t.outcome === "win" ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                    </div>
                    <span className="text-xs sm:text-sm font-bold" style={{ color: (t.profit_loss || t.profit || 0) >= 0 ? 'var(--accent-teal)' : 'var(--accent-red)' }}>
                      {(t.profit_loss || t.profit || 0) >= 0 ? "+" : ""}${Math.abs(t.profit_loss || t.profit || 0).toFixed(2)}
                    </span>
                    <span className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>
                      ${(t.bet_size || t.amount || 0).toFixed(2)}
                    </span>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 min-w-0 overflow-hidden">
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{
                        background: t.direction === "UP" ? 'rgba(0, 229, 168, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                        color: t.direction === "UP" ? 'var(--accent-teal)' : 'var(--accent-red)',
                      }}>{t.direction || "—"}</span>
                      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                        {(t.entry_percent || t.entry_price * 100 || 0).toFixed(1)}%
                      </span>
                      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>S{t.ladder_step}</span>
                    </div>
                    <span data-testid={`trade-row-${i}-outcome`} className="text-[10px] font-semibold truncate" style={{
                      color: t.outcome === "win" ? 'var(--accent-teal)' : 'var(--accent-red)',
                    }}>{["pending", "in_flight"].includes(t.outcome) ? "OPEN" : t.outcome?.toUpperCase()}</span>
                    <span className="text-[10px] flex-shrink-0" style={{ color: 'var(--text-secondary)' }}>{timeAgo(t.timestamp)}</span>
                  </div>
                ))}
                </>)}
              </div>
            </div>
          </div>

          {/* ──── RIGHT COLUMN ──── */}
          <div className="col-span-12 lg:col-span-4 space-y-4">
            {/* Key stats */}
            <div className="grid grid-cols-2 gap-3">
              <div data-testid="stat-daily-pnl" className={`card-base p-4 ${modeDailyPnl >= 0 ? "stat-teal" : "stat-red"}`}>
                <div className="label-style">Daily P/L</div>
                <div className="stat-number" style={{ color: modeDailyPnl >= 0 ? 'var(--accent-teal)' : 'var(--accent-red)' }}>
                  {modeDailyPnl >= 0 ? "+" : ""}${modeDailyPnl.toFixed(2)}
                </div>
                <div className="text-[10px] mt-1" style={{ color: 'var(--text-secondary)' }}>{modeDailyTrades} trades today</div>
              </div>
              <div data-testid="stat-total-pnl" className={`card-base p-4 ${modePnl >= 0 ? "stat-teal" : "stat-red"}`}>
                <div className="label-style">Total P/L</div>
                <div className="stat-number" style={{ color: modePnl >= 0 ? 'var(--accent-teal)' : 'var(--accent-red)' }}>
                  {modePnl >= 0 ? "+" : ""}${modePnl.toFixed(2)}
                </div>
                <div className="text-[10px] mt-1" style={{ color: 'var(--text-secondary)' }}>{modeTotal} total</div>
              </div>
              <div data-testid="stat-win-rate" className="card-base p-4 stat-cyan">
                <div className="label-style">Win Rate</div>
                <div className="stat-number" style={{ color: 'var(--text-primary)' }}>{winRate}%</div>
                <div className="text-[10px] mt-1" style={{ color: 'var(--text-secondary)' }}>
                  <span style={{ color: 'var(--accent-teal)' }}>{modeWins}W</span>{" / "}
                  <span style={{ color: 'var(--accent-red)' }}>{modeLosses}L</span>
                </div>
              </div>
              <div data-testid="stat-streak" className="card-base p-4 stat-cyan">
                <div className="label-style">Streak</div>
                <div className="stat-number" style={{ color: modeStreak > 0 ? 'var(--accent-teal)' : modeStreak < 0 ? 'var(--accent-red)' : 'var(--text-secondary)' }}>
                  {modeStreak > 0 ? "+" : ""}{modeStreak}
                </div>
                <div className="text-[10px] mt-1" style={{ color: 'var(--text-secondary)' }}>
                  Best: {status?.longest_win_streak || 0}W / Worst: {status?.longest_loss_streak || 0}L
                </div>
              </div>
            </div>

            {/* ──── LADDER ──── */}
            <div data-testid="ladder-panel" className="card-base">
              <div className="card-header flex items-center justify-between">
                <span className="label-style mb-0">Ladder</span>
                <span data-testid="ladder-step-indicator" className="text-xs font-bold" style={{ color: !status?.tf_phase || status.tf_phase === "idle" ? 'var(--accent-yellow)' : 'var(--accent-teal)' }}>{!status?.tf_phase || status.tf_phase === "idle" ? (status?.tf_armed === false ? "WAIT RED→GREEN" : "WAIT GREEN") : status.tf_phase === "bet1" ? "BET 1 DOWN" : "BET 2 UP"}</span>
              </div>
              <div className="card-content space-y-1.5">
                <div data-testid="fade-phase-banner" className="text-[10px] leading-relaxed px-3 py-2 rounded-lg mb-1"
                  style={{ background: 'rgba(0,229,168,0.06)', border: '1px solid rgba(0,229,168,0.2)', color: 'var(--text-secondary)' }}>
                  {!status?.tf_phase || status.tf_phase === "idle"
                    ? (status?.tf_armed === false ? "Cycle done. Waiting for a red candle, then a new green, before betting again." : "Waiting for a green candle. Then bet 1 DOWN on the next market.")
                    : status.tf_phase === "bet1" ? `Bet 1: DOWN $${ladder[0]} on the next market.` : `Bet 1 lost. Bet 2: UP $${ladder[1]} on the next market.`}
                </div>
                {ladder.map((amt, i) => {
                  const s = i + 1;
                  const inCycle = status?.tf_phase === "bet1" || status?.tf_phase === "bet2";
                  const active = inCycle && s === step;
                  const past = inCycle && s < step;
                  return (
                    <div key={s} className="flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all"
                      style={{
                        background: active ? 'rgba(0, 229, 168, 0.06)' : past ? 'rgba(239, 68, 68, 0.04)' : 'transparent',
                        border: `1px solid ${active ? 'rgba(0, 229, 168, 0.25)' : past ? 'rgba(239, 68, 68, 0.12)' : 'rgba(30, 42, 58, 0.4)'}`,
                      }}>
                      <div className="w-6 h-6 flex items-center justify-center text-[10px] font-bold rounded-md"
                        style={{
                          background: active ? 'var(--accent-teal)' : past ? 'rgba(239, 68, 68, 0.15)' : 'rgba(30, 42, 58, 0.6)',
                          color: active ? '#0B0F1A' : past ? 'var(--accent-red)' : 'var(--text-secondary)',
                        }}>{s}</div>
                      <span className="text-sm font-semibold" style={{
                        color: active ? 'var(--accent-teal)' : past ? 'rgba(239, 68, 68, 0.5)' : 'var(--text-secondary)',
                      }}>${amt.toFixed(2)}</span>
                      {active && <ChevronRight className="w-3.5 h-3.5 ml-auto" style={{ color: 'var(--accent-teal)' }} />}
                      {past && <span className="ml-auto text-[9px] font-semibold" style={{ color: 'rgba(239, 68, 68, 0.4)' }}>LOST</span>}
                    </div>
                  );
                })}
                <div className="pt-3 mt-3 space-y-2" style={{ borderTop: '1px solid var(--border)' }}>
                  <div className="flex justify-between text-xs">
                    <span style={{ color: 'var(--text-secondary)' }}>Next Bet</span>
                    <span className="font-bold" style={{ color: 'var(--accent-teal)' }}>${(ladder[step - 1] || ladder[0]).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span style={{ color: 'var(--text-secondary)' }}>Exposure</span>
                    <span style={{ color: 'var(--text-primary)' }}>${ladder.reduce((a, b) => a + b, 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span style={{ color: 'var(--text-secondary)' }}>Bankroll</span>
                    <span style={{ color: 'var(--text-primary)' }}>${(settings?.bankroll || 1000).toFixed(2)}</span>
                  </div>
                  {status?.ladder_failures > 0 && (
                    <div className="flex justify-between text-xs">
                      <span style={{ color: 'var(--text-secondary)' }}>Failures</span>
                      <span style={{ color: 'var(--accent-yellow)' }}>{status.ladder_failures}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
